import React, { useState } from 'react';
import { theme, fontStack, bodyFont } from '../theme';
import { FadeIn, Divider, CrossIcon } from '../components/shared';
import churchConfig from '../churchConfig';
import { useChurchData, useChurchInfo } from '../data/ChurchDataProvider';

/* ── Payment Method Card ── */
function PaymentCard({ method }) {
  const iconMap = {
    "text-to-give": "📱",
    "online": "💳",
    "paypal": "🅿️",
    "venmo": "📲",
    "cashapp": "💵",
    "qr-code": "📷",
    "embed": "🌐",
    "link": "🔗",
    "other": "💝",
  };

  return (
    <div style={{
      padding: 28, background: theme.bgCard, border: `1px solid ${theme.border}`,
      transition: "all 0.3s",
    }}
      onMouseEnter={e => e.currentTarget.style.borderColor = theme.gold}
      onMouseLeave={e => e.currentTarget.style.borderColor = theme.border}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <span style={{ fontSize: 32 }}>{method.icon || iconMap[method.type] || "💝"}</span>
        <h3 style={{ fontFamily: fontStack, fontSize: 22, fontWeight: 500, color: theme.cream }}>{method.label}</h3>
      </div>

      {method.instructions && (
        <p style={{ fontFamily: bodyFont, fontSize: 15, color: theme.textMuted, lineHeight: 1.7, marginBottom: 16 }}>
          {method.instructions}
        </p>
      )}

      {method.embedCode && (
        <div
          style={{ marginBottom: 16 }}
          dangerouslySetInnerHTML={{ __html: method.embedCode }}
        />
      )}

      {method.link && (
        <a href={method.link} target="_blank" rel="noopener noreferrer"
          style={{
            display: "inline-block", padding: "12px 32px",
            background: theme.gold, color: theme.bg,
            fontFamily: bodyFont, fontSize: 12, fontWeight: 600,
            letterSpacing: "0.15em", textTransform: "uppercase",
            textDecoration: "none", transition: "all 0.3s",
          }}
          onMouseEnter={e => e.currentTarget.style.opacity = "0.85"}
          onMouseLeave={e => e.currentTarget.style.opacity = "1"}
        >
          {method.linkLabel || "Give Now"}
        </a>
      )}

      {method.qrImage && (
        <div style={{ textAlign: "center", marginTop: 12 }}>
          <img src={method.qrImage} alt={`${method.label} QR Code`}
            style={{ maxWidth: 200, border: `2px solid ${theme.border}`, padding: 8, background: "#fff" }}
          />
        </div>
      )}
    </div>
  );
}

/* ── Fundraiser Campaign Card ── */
function FundraiserCard({ campaign }) {
  const progress = campaign.goalAmount > 0 ? Math.min(100, (campaign.currentAmount / campaign.goalAmount) * 100) : 0;
  const isExpired = campaign.deadline && new Date(campaign.deadline) < new Date();
  const daysLeft = campaign.deadline ? Math.max(0, Math.ceil((new Date(campaign.deadline) - new Date()) / (1000 * 60 * 60 * 24))) : null;

  return (
    <div style={{
      padding: 28, background: theme.bgCard, border: `1px solid ${theme.border}`,
      opacity: isExpired ? 0.6 : 1,
    }}>
      {campaign.image && (
        <div style={{
          height: 180, marginBottom: 20, background: `url(${campaign.image}) center/cover`,
          borderRadius: 2, border: `1px solid ${theme.border}`,
        }} />
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
        <h3 style={{ fontFamily: fontStack, fontSize: 22, fontWeight: 500, color: theme.cream, flex: 1 }}>{campaign.title}</h3>
        {isExpired && (
          <span style={{ fontFamily: bodyFont, fontSize: 10, fontWeight: 600, color: theme.textMuted, background: "rgba(255,255,255,0.05)", padding: "4px 10px", letterSpacing: "0.1em", textTransform: "uppercase", flexShrink: 0 }}>Ended</span>
        )}
      </div>

      <p style={{ fontFamily: bodyFont, fontSize: 14, color: theme.textMuted, lineHeight: 1.7, marginBottom: 20 }}>
        {campaign.description}
      </p>

      {/* Progress bar */}
      {campaign.goalAmount > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
            <span style={{ fontFamily: fontStack, fontSize: 24, fontWeight: 600, color: theme.gold }}>
              ${(campaign.currentAmount || 0).toLocaleString()}
            </span>
            <span style={{ fontFamily: bodyFont, fontSize: 14, color: theme.textMuted, alignSelf: "flex-end" }}>
              of ${campaign.goalAmount.toLocaleString()} goal
            </span>
          </div>
          <div style={{ width: "100%", height: 8, background: "rgba(255,255,255,0.05)", borderRadius: 4, overflow: "hidden" }}>
            <div style={{
              width: `${progress}%`, height: "100%",
              background: `linear-gradient(90deg, ${theme.goldDark}, ${theme.gold})`,
              borderRadius: 4, transition: "width 1s ease",
            }} />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
            <span style={{ fontFamily: bodyFont, fontSize: 12, color: theme.gold }}>{Math.round(progress)}% funded</span>
            {daysLeft !== null && !isExpired && (
              <span style={{ fontFamily: bodyFont, fontSize: 12, color: theme.textMuted }}>{daysLeft} days remaining</span>
            )}
          </div>
        </div>
      )}

      {campaign.donateLink && !isExpired && (
        <a href={campaign.donateLink} target="_blank" rel="noopener noreferrer"
          style={{
            display: "inline-block", padding: "10px 28px",
            background: "transparent", border: `1px solid ${theme.gold}`, color: theme.gold,
            fontFamily: bodyFont, fontSize: 12, fontWeight: 600,
            letterSpacing: "0.15em", textTransform: "uppercase",
            textDecoration: "none", transition: "all 0.3s",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = theme.gold; e.currentTarget.style.color = theme.bg; }}
          onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = theme.gold; }}
        >
          Contribute
        </a>
      )}
    </div>
  );
}

/* ── Main Giving Page ── */
export default function GivingPage() {
  const { data } = useChurchData();
  const info = useChurchInfo();
  const enabledMethods = (data.paymentMethods || []).filter(m => m.enabled);
  const activeFundraisers = (data.fundraisers || []).filter(f => f.active);
  const hasActiveCampaigns = activeFundraisers.length > 0;

  return (
    <div style={{ paddingTop: 100 }}>
      {/* Header */}
      <section style={{ padding: "80px 24px 40px", textAlign: "center" }}>
        <FadeIn>
          <div style={{ fontFamily: bodyFont, fontSize: 11, fontWeight: 500, color: theme.gold, letterSpacing: "0.3em", textTransform: "uppercase", marginBottom: 16 }}>Support Our Ministry</div>
          <h1 style={{ fontFamily: fontStack, fontSize: "clamp(36px, 6vw, 64px)", fontWeight: 300, color: theme.cream, lineHeight: 1.1 }}>
            <span style={{ fontStyle: "italic", fontWeight: 600, color: theme.gold }}>Give</span>
          </h1>
          <Divider width={60} />
          <p style={{ fontFamily: bodyFont, fontSize: 16, color: theme.textMuted, maxWidth: 560, margin: "16px auto 0", lineHeight: 1.7 }}>
            Your generous giving supports our church family, community outreach, and the spreading of the Gospel. Every gift makes a difference.
          </p>
        </FadeIn>
      </section>

      {/* Scripture */}
      <section style={{ padding: "0 24px 60px", textAlign: "center" }}>
        <FadeIn delay={0.1}>
          <div style={{ maxWidth: 600, margin: "0 auto", padding: "24px 32px", background: `linear-gradient(135deg, rgba(200,164,86,0.06) 0%, transparent 100%)`, border: `1px solid ${theme.border}` }}>
            <p style={{ fontFamily: fontStack, fontSize: 18, fontStyle: "italic", color: theme.cream, lineHeight: 1.6, marginBottom: 8 }}>
              "Each of you should give what you have decided in your heart to give, not reluctantly or under compulsion, for God loves a cheerful giver."
            </p>
            <span style={{ fontFamily: bodyFont, fontSize: 13, color: theme.gold }}>— 2 Corinthians 9:7</span>
          </div>
        </FadeIn>
      </section>

      {/* Payment Methods */}
      {enabledMethods.length > 0 && (
        <section style={{ padding: "0 24px 80px" }}>
          <div style={{ maxWidth: 900, margin: "0 auto" }}>
            <FadeIn>
              <div style={{ textAlign: "center", marginBottom: 40 }}>
                <h2 style={{ fontFamily: fontStack, fontSize: "clamp(24px, 4vw, 36px)", fontWeight: 400, color: theme.cream }}>Ways to Give</h2>
              </div>
            </FadeIn>
            <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(${enabledMethods.length === 1 ? '400px' : '280px'}, 1fr))`, gap: 20 }}>
              {enabledMethods.map((method, i) => (
                <FadeIn key={method.id} delay={i * 0.1}>
                  <PaymentCard method={method} />
                </FadeIn>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Fundraiser Campaigns */}
      {hasActiveCampaigns && (
        <section style={{ padding: "0 24px 80px" }}>
          <div style={{ maxWidth: 900, margin: "0 auto" }}>
            <FadeIn>
              <div style={{ textAlign: "center", marginBottom: 40 }}>
                <div style={{ fontFamily: bodyFont, fontSize: 11, fontWeight: 500, color: theme.gold, letterSpacing: "0.3em", textTransform: "uppercase", marginBottom: 12 }}>Current Campaigns</div>
                <h2 style={{ fontFamily: fontStack, fontSize: "clamp(24px, 4vw, 36px)", fontWeight: 400, color: theme.cream }}>Fundraisers</h2>
                <Divider />
              </div>
            </FadeIn>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 20 }}>
              {activeFundraisers.map((campaign, i) => (
                <FadeIn key={campaign.id} delay={i * 0.1}>
                  <FundraiserCard campaign={campaign} />
                </FadeIn>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contact */}
      <section style={{ padding: "60px 24px 100px", textAlign: "center" }}>
        <FadeIn>
          <div style={{ maxWidth: 500, margin: "0 auto" }}>
            <CrossIcon size={24} color={theme.gold} />
            <p style={{ fontFamily: bodyFont, fontSize: 15, color: theme.textMuted, lineHeight: 1.7, marginTop: 16 }}>
              Questions about giving? Contact the church office at <strong style={{ color: theme.cream }}>{info.phone}</strong> or speak with Pastor Trey.
            </p>
          </div>
        </FadeIn>
      </section>
    </div>
  );
}
